import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Clock, AlertTriangle, CheckCircle, UserPlus, Calendar, ChevronRight, Archive } from 'lucide-react';
import { notificationAPI, taskAPI, assignmentAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { format, differenceInDays, isAfter } from 'date-fns';
import { parseLocalDate } from '../lib/dateUtils';

const NotificationDropdown = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<unknown[]>([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load unread count on mount for badge
  useEffect(() => {
    loadNotifications();
  }, []);

  // Load notifications and upcoming deadlines when dropdown opens
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
      loadUpcomingDeadlines();
    }
  }, [isOpen]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.list({ limit: 10 });
      setNotifications(response.data || []);
    } catch (error: unknown) {
      console.error('Failed to load notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUpcomingDeadlines = async () => {
    try {
      // Get tasks and assignments to find upcoming deadlines
      const [tasksRes, assignmentsRes] = await Promise.all([
        taskAPI.list({ limit: 100 }),
        assignmentAPI.list({ limit: 100 })
      ]);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tasks = tasksRes.data || [];
      const assignments = assignmentsRes.data || [];

      // Find tasks with upcoming deadlines (next 7 days)
      const upcoming: Record<string, unknown>[] = [];

      tasks.forEach(task => {
        if (!task.client_end_date || task.status === 'Completed' || task.status === 'Archive') return;
        const endDate = parseLocalDate(task.client_end_date)!;
        const daysUntil = differenceInDays(endDate, today);
        
        if (daysUntil >= -7 && daysUntil <= 7) {
          upcoming.push({
            id: task.id,
            type: 'task',
            title: task.task_name,
            subtitle: task.project_name,
            dueDate: endDate,
            daysUntil,
            status: task.status,
            isOverdue: daysUntil < 0
          });
        }
      });

      // Filter for user's assignments if they have an employee_id
      if (user?.employee_id) {
        const myAssignments = assignments.filter(a => a.employee_id === user?.employee_id);
        myAssignments.forEach(assignment => {
          if (!assignment.end_date || assignment.status === 'Archive') return;
          const endDate = parseLocalDate(assignment.end_date)!;
          const daysUntil = differenceInDays(endDate, today);
          
          if (daysUntil >= -7 && daysUntil <= 7) {
            // Check if already added via task
            if (!upcoming.some(u => u.id === assignment.task_id && u.type === 'task')) {
              upcoming.push({
                id: assignment.id,
                type: 'assignment',
                title: assignment.task_name || 'Assignment',
                subtitle: `${assignment.budgeted_hours}h budgeted`,
                dueDate: endDate,
                daysUntil,
                status: assignment.status,
                isOverdue: daysUntil < 0
              });
            }
          }
        });
      }

      // Sort by due date
      upcoming.sort((a, b) => a.daysUntil - b.daysUntil);
      setUpcomingDeadlines(upcoming.slice(0, 8));
    } catch (error: unknown) {
      console.error('Failed to load upcoming deadlines:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationAPI.markRead(notificationId);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error: unknown) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'assignment': return <UserPlus className="text-alliance-steel" size={16}  />;      case 'deadline': return <Clock className="text-status-warning" size={16}  />;      case 'overdue': return <AlertTriangle className="text-status-error" size={16}  />;      case 'completed': return <CheckCircle className="text-status-success" size={16}  />;      case 'purge_warning': return <Archive className="text-status-error" size={16}  />;      default: return <Bell className="text-wmt-secondary" size={16}  />;    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const urgentCount = upcomingDeadlines.filter(d => d.isOverdue || d.daysUntil <= 2).length;
  const totalBadgeCount = unreadCount + urgentCount;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg relative transition"
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {totalBadgeCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold text-white"
            style={{ background: urgentCount > 0 ? 'var(--error-text)' : 'var(--alliance-steel)' }}
          >
            {totalBadgeCount > 9 ? '9+' : totalBadgeCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-96 rounded-xl shadow-2xl overflow-hidden z-50 bg-wmt-secondary"
          
        >
          {/* Header */}
          <div className="p-4 border-b flex items-center justify-between border-wmt-secondary bg-wmt-tertiary" >            <h3 className="font-semibold text-wmt-primary" >Notifications</h3>            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded transition"
            >
              <X size={16} />
            </button>
          </div>

          {/* Content */}
          <div className="max-h-[70vh] overflow-y-auto">
            {/* Upcoming Deadlines Section */}
            {upcomingDeadlines.length > 0 && (
              <div className="p-3 border-b border-wmt-secondary" >                <h4 className="text-xs font-semibold uppercase mb-2 flex items-center gap-2 text-wmt-muted" >                  <Calendar size={14} />
                  Upcoming Deadlines
                </h4>
                <div className="space-y-2">
                  {upcomingDeadlines.map((item) => (
                    <div
                      key={`${item.type}-${item.id}`}
                      className="p-3 rounded-lg transition cursor-pointer"
                      style={{ 
                        background: item.isOverdue ? 'var(--error-bg)' : item.daysUntil <= 2 ? 'var(--warning-bg)' : 'var(--bg-tertiary)',
                        border: `1px solid ${item.isOverdue ? 'var(--error-text)' : item.daysUntil <= 2 ? 'var(--warning-text)' : 'var(--border-secondary)'}`
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {item.isOverdue ? (
                              <AlertTriangle className="text-status-error" size={14}  />                            ) : item.daysUntil <= 2 ? (
                              <Clock className="text-status-warning" size={14}  />                            ) : (
                              <Calendar className="text-alliance-steel" size={14}  />                            )}
                            <span 
                              className="text-xs font-medium px-2 py-0.5 rounded"
                              style={{ 
                                background: item.isOverdue ? 'var(--error-bg)' : item.daysUntil <= 2 ? 'var(--warning-bg)' : 'var(--info-bg)',
                                color: item.isOverdue ? 'var(--error-text)' : item.daysUntil <= 2 ? 'var(--warning-text)' : 'var(--alliance-steel)'
                              }}
                            >
                              {item.isOverdue 
                                ? `${Math.abs(item.daysUntil)} day${Math.abs(item.daysUntil) !== 1 ? 's' : ''} overdue`
                                : item.daysUntil === 0 
                                  ? 'Due today!'
                                  : `${item.daysUntil} day${item.daysUntil !== 1 ? 's' : ''} left`
                              }
                            </span>
                          </div>
                          <p className="text-sm font-medium truncate text-wmt-primary" >                            {item.title}
                          </p>
                          <p className="text-xs truncate text-wmt-muted" >                            {item.subtitle} • Due {format(item.dueDate, 'MMM d')}
                          </p>
                        </div>
                        <ChevronRight className="text-wmt-light" size={16}  />                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notifications Section */}
            <div className="p-3 bg-wmt-tertiary" >              <h4 className="text-xs font-semibold uppercase mb-2 flex items-center gap-2 text-wmt-muted" >                <Bell size={14} />
                Recent Activity
              </h4>
              
              {loading ? (
                <div className="text-center py-6">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : notifications.length > 0 ? (
                <div className="space-y-2">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => !notification.is_read && markAsRead(notification.id)}
                      className={`p-3 rounded-lg transition cursor-pointer`}
                      style={{ 
                        background: !notification.is_read ? 'var(--info-bg)' : 'var(--bg-secondary)',
                        border: '1px solid var(--border-secondary)' 
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-wmt-hover" >                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate text-wmt-primary" >                            {notification.title}
                          </p>
                          <p className="text-xs truncate text-wmt-muted" >                            {notification.message}
                          </p>
                          <p className="text-xs mt-1 text-wmt-light" >                            {notification.created_at && format(new Date(notification.created_at), 'MMM d, h:mm a')}
                          </p>
                        </div>
                        {!notification.is_read && (
                          <div className="w-2 h-2 rounded-full flex-shrink-0 bg-alliance-steel"  />                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Bell size={32} className="mx-auto mb-2 text-wmt-light"  />                  <p className="text-sm text-wmt-muted" >No notifications yet</p>                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          {(notifications.length > 0 || upcomingDeadlines.length > 0) && (
            <div className="p-3 border-t text-center border-wmt-secondary bg-wmt-secondary" >              <button 
                className="text-sm font-medium hover:underline"
                onClick={() => setIsOpen(false)}
              >
                View All Activity
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
